import React, { useCallback, useMemo } from 'react';
import { Plus, ChevronDown, ChevronRight } from 'lucide-react';
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  type DragEndEvent,
} from '@dnd-kit/core';
import {
  SortableContext,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import type { SectionWithLessons, Lesson } from '../types';
import LessonItem from './LessonItem';

interface SectionCardProps {
  section: SectionWithLessons;
  index: number;
  onToggle: (sectionId: number) => void;
  onAddLesson: (sectionId: number) => void;
  onEditLesson: (lesson: Lesson) => void;
  onDeleteLesson: (lessonId: number | string) => void;
  onReorderLessons?: (sectionId: number, reorderedLessons: Lesson[]) => void;
}

const SectionCard: React.FC<SectionCardProps> = ({
  section,
  index,
  onToggle,
  onAddLesson,
  onEditLesson,
  onDeleteLesson,
  onReorderLessons,
}) => {
  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: { distance: 5 },
    }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  const lessonIds = useMemo(
    () => section.lessons.map((l) => l.id!),
    [section.lessons]
  );

  const handleToggle = useCallback(() => {
    if (section.id) onToggle(section.id);
  }, [section.id, onToggle]);

  const handleAddLesson = useCallback((e: React.MouseEvent) => {
    e.stopPropagation();
    if (section.id) onAddLesson(section.id);
  }, [section.id, onAddLesson]);

  const handleDragEnd = useCallback((event: DragEndEvent) => {
    const { active, over } = event;
    if (!over || active.id === over.id || !section.id) return;

    const oldIndex = section.lessons.findIndex((l) => l.id === active.id);
    const newIndex = section.lessons.findIndex((l) => l.id === over.id);
    if (oldIndex === -1 || newIndex === -1) return;

    const reordered = [...section.lessons];
    const [moved] = reordered.splice(oldIndex, 1);
    reordered.splice(newIndex, 0, moved);

    onReorderLessons?.(section.id, reordered);
  }, [section.id, section.lessons, onReorderLessons]);

  return (
    <div style={{
      border: '1px solid #ddd',
      borderRadius: '8px',
      marginBottom: '15px',
      overflow: 'hidden',
    }}>
      {/* Section Header */}
      <div
        onClick={handleToggle}
        style={{
          cursor: 'pointer',
          padding: '15px 20px',
          backgroundColor: '#fff',
          borderBottom: section.isExpanded ? '1px solid #ddd' : 'none',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: '12px', flex: 1 }}>
          {section.isExpanded
            ? <ChevronDown size={20} style={{ color: '#FF8C00' }} />
            : <ChevronRight size={20} style={{ color: '#666' }} />
          }
          <h3 style={{ margin: 0, fontSize: '16px', fontWeight: '600', color: '#333', flex: 1 }}>
            Section {index + 1}: {section.title}
          </h3>
          <span style={{
            backgroundColor: '#e3f2fd',
            color: '#1976d2',
            padding: '4px 12px',
            borderRadius: '12px',
            fontSize: '13px',
            fontWeight: '500',
          }}>
            {section.lessons.length} lesson{section.lessons.length !== 1 ? 's' : ''}
          </span>
        </div>
        <button
          onClick={handleAddLesson}
          className="btn-icon"
          title="Add Lesson to this Section"
          style={{
            backgroundColor: '#4CAF50',
            color: 'white',
            padding: '8px',
            borderRadius: '6px',
            border: 'none',
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'center',
            gap: '5px',
            marginLeft: '12px',
          }}
        >
          <Plus size={18} />
        </button>
      </div>

      {/* Section Content (Expanded) */}
      {section.isExpanded && (
        <div style={{ backgroundColor: '#fafafa', padding: '15px 20px' }}>
          {section.lessons.length === 0 ? (
            <div style={{ textAlign: 'center', padding: '30px', color: '#999' }}>
              <p>No lessons in this section yet. Click the button below to add lessons.</p>
            </div>
          ) : (
            <DndContext
              sensors={sensors}
              collisionDetection={closestCenter}
              onDragEnd={handleDragEnd}
            >
              <SortableContext items={lessonIds} strategy={verticalListSortingStrategy}>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                  {section.lessons.map((lesson, lessonIndex) => (
                    <LessonItem
                      key={lesson.id}
                      lesson={lesson}
                      index={lessonIndex}
                      onEdit={onEditLesson}
                      onDelete={onDeleteLesson}
                    />
                  ))}
                </div>
              </SortableContext>
            </DndContext>
          )}

          <div style={{ marginTop: '15px' }}>
            <button
              onClick={() => section.id && onAddLesson(section.id)}
              className="btn-secondary"
              style={{
                width: '100%',
                backgroundColor: '#4CAF50',
                color: 'white',
                border: 'none',
                padding: '12px 20px',
                borderRadius: '6px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: '8px',
                fontSize: '14px',
                fontWeight: '500',
                cursor: 'pointer',
              }}
            >
              <Plus size={18} />
              Add Lesson to this Section
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default React.memo(SectionCard);
